import { useSearchParams } from 'react-router-dom';
import { useQuery, type UseQueryResult } from '@tanstack/react-query';
import {
  rotuloDeLotacao,
  type AulasDoDia,
  type RiscoDeCancelamento,
  type TurmaNaLista,
} from '@arena/shared/schemas';
import { horaNaArena } from '../agenda/formatos.js';
import { ModalDoAluno } from '../alunos/ModalDoAluno.jsx';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Barras } from '../graficos/Barras.jsx';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { Avatar, Botao, Card, Nota, Status, Vazio } from '../ui/primitivos.jsx';
import { useDica } from '../ui/Dica.js';
import { corDaLotacao, diasCurtos, gradeResumida } from './formatos.js';
import { ModalDaTurma } from './ModalDaTurma.jsx';
import { ModalDeChamada } from './ModalDeChamada.jsx';
import { ModalDeConflito } from './ModalDeConflito.jsx';
import { ModalDeTurma } from './ModalDeTurma.jsx';

/**
 * `#v-turmas` — os cards com semáforo, a frequência das 8 semanas, o card
 * de risco de cancelamento e, para quem decide, o aviso da pendência P1.
 *
 * O primeiro bloco é "Aulas de hoje": é o caminho do professor no celular
 * — abrir a tela no fim da aula e marcar. Os cards de turma vêm depois,
 * como no protótipo.
 *
 * O que cada perfil vê é o servidor que decide: o Professor recebe só as
 * turmas dele, e os botões de cadastro somem pela matriz (cosmético — o
 * servidor recusa de qualquer jeito).
 *
 * Os modais moram na URL (`?turma=`, `?chamada=`, `?nova=1`, `?p1=1`),
 * como os de alunos: F5 e voltar do navegador fazem o esperado.
 *
 * Ver: Arena_backend/Docs/plano/10-turmas-professores-chamada.md
 */
export function PaginaDeTurmas() {
  const { pode } = useSessao();
  const [params, setParams] = useSearchParams();

  const lista = useQuery({ queryKey: ['turmas', 'lista'], queryFn: () => turmasApi.listar() });
  const frequencia = useQuery({
    queryKey: ['turmas', 'frequencia'],
    queryFn: () => turmasApi.frequencia(),
  });
  const risco = useQuery({ queryKey: ['turmas', 'risco'], queryFn: () => turmasApi.risco() });
  const aulas = useQuery({
    queryKey: ['turmas', 'aulas-de-hoje'],
    queryFn: () => turmasApi.aulasDoDia(),
    // A chamada é feita no fim da aula: o resumo "feita / pendente"
    // precisa andar sem F5 enquanto a tela está aberta.
    refetchInterval: 60_000,
  });
  const alocacao = useQuery({
    queryKey: ['turmas', 'alocacoes'],
    queryFn: () => turmasApi.quadroDeAlocacao(),
    enabled: pode('turmas', 'editar'),
    retry: false,
  });

  function mudarParams(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }
  const abrir = (chave: string, valor: string) =>
    mudarParams((p) => {
      p.delete('turma');
      p.delete('chamada');
      p.delete('nova');
      p.delete('p1');
      p.delete('aluno');
      p.set(chave, valor);
    });
  const fechar = (chave: string) => mudarParams((p) => p.delete(chave));

  const turmaAberta = params.get('turma');
  const chamadaAberta = params.get('chamada');
  const nova = params.get('nova') === '1';
  const p1 = params.get('p1') === '1';
  const alunoAberto = params.get('aluno');

  const pendentesDaP1 = (alocacao.data?.sobreposicoes ?? []).filter((s) => !s.alocado);
  const ativas = (lista.data?.turmas ?? []).filter((t) => t.status === 'Ativa');
  const vagas = ativas.reduce((soma, t) => soma + t.capacidade, 0);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Turmas e chamada</h1>
          <p>
            {lista.data
              ? `${ativas.length} turma${ativas.length === 1 ? '' : 's'} ativa${ativas.length === 1 ? '' : 's'} · ${vagas} vagas.`
              : 'Turmas, lotação e frequência.'}
          </p>
        </div>
        {pode('turmas', 'criar') && (
          <div className="head-actions">
            <Botao icone="plus" onClick={() => abrir('nova', '1')}>
              Nova turma
            </Botao>
          </div>
        )}
      </div>

      <Nota>
        <b>Módulo 3 — Professores e turmas.</b> Lotação por cor: até metade das vagas verde, até 80%
        amarelo, acima disso vermelho — com dez vagas, os cortes do protótipo (5, 8, 10). Ao lotar,
        a lista de espera abre sozinha. A chamada é feita pelo professor no celular e três faltas
        seguidas acendem o alerta de risco de cancelamento.
      </Nota>

      {pendentesDaP1.length > 0 && (
        <div className="aviso conflito" role="status">
          <Icone nome="info" />
          <div>
            <b>Conflito a resolver · P1.</b>{' '}
            {pendentesDaP1.length === 1
              ? 'Um horário tem modalidades diferentes ocupando todas as quadras'
              : `${pendentesDaP1.length} horários têm modalidades diferentes ocupando todas as quadras`}
            , sem alocação fixa definida.{' '}
            <button type="button" className="link" onClick={() => abrir('p1', '1')}>
              Definir alocação
            </button>
          </div>
        </div>
      )}

      <AulasDeHoje consulta={aulas} onChamada={(id) => abrir('chamada', id)} />

      {lista.isPending && (
        <div className="grid g3">
          {[0, 1, 2].map((i) => (
            <EsqueletoDeCard key={i} linhas={3} />
          ))}
        </div>
      )}
      {lista.isError && (
        <Erro
          tipo={
            lista.error instanceof ErroDeRede
              ? 'rede'
              : lista.error instanceof ErroDeApi && lista.error.ehPermissao
                ? 'permissao'
                : 'falha'
          }
          detalhe={mensagemDoErro(lista.error)}
          onTentarDeNovo={() => void lista.refetch()}
        />
      )}
      {lista.data && lista.data.turmas.length === 0 && (
        <Vazio
          acao={
            pode('turmas', 'criar') ? (
              <Botao icone="plus" onClick={() => abrir('nova', '1')}>
                Criar a primeira turma
              </Botao>
            ) : undefined
          }
        >
          {pode('turmas', 'criar')
            ? 'Nenhuma turma cadastrada ainda.'
            : 'Nenhuma turma sua por aqui.'}
        </Vazio>
      )}
      {lista.data && lista.data.turmas.length > 0 && (
        <div className="grid g3">
          {lista.data.turmas.map((turma) => (
            <CardDaTurma
              key={turma.id}
              turma={turma}
              onAbrir={() => abrir('turma', turma.id)}
              onChamada={(id) => abrir('chamada', id)}
            />
          ))}
        </div>
      )}

      <div className="grid g-2-1">
        <Card titulo="Frequência das últimas 8 semanas" dica="passe o mouse nas barras">
          {frequencia.isPending && <EsqueletoDeCard linhas={2} />}
          {frequencia.isError && <Erro detalhe={mensagemDoErro(frequencia.error)} />}
          {frequencia.data &&
            (frequencia.data.semanas.every((s) => s.presencas + s.faltas === 0) ? (
              <Vazio>Nenhuma chamada registrada nas últimas 8 semanas.</Vazio>
            ) : (
              <Barras
                cores={['var(--serie-1)', 'var(--dim)']}
                legenda={[
                  { rotulo: 'Presenças', cor: 'var(--serie-1)' },
                  { rotulo: 'Faltas', cor: 'var(--dim)' },
                ]}
                colunas={frequencia.data.semanas.map((s) => {
                  const total = s.presencas + s.faltas;
                  const pct = total > 0 ? Math.round((s.faltas / total) * 100) : 0;
                  return {
                    x: s.rotulo,
                    valores: [s.presencas, s.faltas],
                    dicas: [
                      `${s.presencas} presença${s.presencas === 1 ? '' : 's'} · ${s.de.slice(8, 10)}/${s.de.slice(5, 7)} a ${s.ate.slice(8, 10)}/${s.ate.slice(5, 7)}`,
                      `${s.faltas} falta${s.faltas === 1 ? '' : 's'} · ${pct}% do total`,
                    ],
                  };
                })}
              />
            ))}
        </Card>

        <CardDeRisco
          consulta={risco}
          turmas={lista.data?.turmas ?? []}
          onAbrirAluno={(id) => abrir('aluno', id)}
        />
      </div>

      {nova && pode('turmas', 'criar') && (
        <ModalDeTurma onFechar={() => fechar('nova')} onCriada={(id) => abrir('turma', id)} />
      )}
      {turmaAberta && (
        <ModalDaTurma
          id={turmaAberta}
          onFechar={() => fechar('turma')}
          onChamada={(id) => abrir('chamada', id)}
        />
      )}
      {chamadaAberta && (
        <ModalDeChamada marcacaoId={chamadaAberta} onFechar={() => fechar('chamada')} />
      )}
      {p1 && pode('turmas', 'editar') && <ModalDeConflito onFechar={() => fechar('p1')} />}
      {alunoAberto && <ModalDoAluno id={alunoAberto} onFechar={() => fechar('aluno')} />}
    </>
  );
}

function CardDaTurma({
  turma,
  onAbrir,
  onChamada,
}: {
  turma: TurmaNaLista;
  onAbrir: () => void;
  onChamada: (marcacaoId: string) => void;
}) {
  const { pode } = useSessao();
  const dica = useDica();
  const cor = corDaLotacao(turma.matriculados, turma.capacidade);
  const ocupacao = Math.min(
    100,
    Math.round((turma.matriculados / Math.max(1, turma.capacidade)) * 100),
  );

  return (
    <article
      className="card clickable"
      onClick={onAbrir}
      onMouseEnter={(e) =>
        dica.mostrar(e, {
          titulo: `${turma.titulo}`,
          texto: `${turma.matriculados} de ${turma.capacidade} alunos<br><em>${rotuloDeLotacao(turma.matriculados, turma.capacidade)}</em>`,
        })
      }
      onMouseMove={(e) => dica.mover(e)}
      onMouseLeave={() => dica.esconder()}
    >
      <div className="card-head" style={{ marginBottom: 10 }}>
        <h2>{turma.titulo}</h2>
        {turma.status === 'Ativa' ? (
          <span
            className="status"
            style={{ background: `color-mix(in srgb, ${cor} 14%, transparent)`, color: cor }}
          >
            {turma.matriculados}/{turma.capacidade}
          </span>
        ) : (
          <Status situacao={turma.status === 'Suspensa' ? 'atencao' : 'neutra'}>
            {turma.status}
          </Status>
        )}
      </div>
      <p className="sub">{gradeResumida(turma.grade)}</p>
      <p className="sub" style={{ marginTop: 4 }}>
        {turma.professor.nome}
        {turma.grade ? ` · ${turma.grade.quadra.nome}` : ''}
        {turma.esperando > 0 ? ` · ${turma.esperando} na espera` : ''}
      </p>
      <div className="trilho-de-lotacao" aria-hidden="true">
        <div style={{ width: `${ocupacao}%`, background: cor }} />
      </div>
      <div className="acoes-do-card">
        {turma.aulaParaChamada && pode('turmas', 'ler') && (
          <Botao
            variante="fantasma"
            pequeno
            onClick={(e) => {
              e.stopPropagation();
              onChamada(turma.aulaParaChamada!.marcacaoId);
            }}
          >
            {turma.aulaParaChamada.chamadaFeita ? 'Chamada feita' : 'Chamada'}
          </Botao>
        )}
        <Botao
          variante="contorno"
          pequeno
          onClick={(e) => {
            e.stopPropagation();
            onAbrir();
          }}
        >
          Alunos
        </Botao>
      </div>
    </article>
  );
}

function AulasDeHoje({
  consulta,
  onChamada,
}: {
  consulta: UseQueryResult<AulasDoDia>;
  onChamada: (marcacaoId: string) => void;
}) {
  const { pode } = useSessao();
  if (consulta.isPending) return <EsqueletoDeCard linhas={2} />;
  if (consulta.isError) return null;
  const vivas = consulta.data.aulas.filter((a) => !a.cancelada);
  if (vivas.length === 0) return null;

  return (
    <Card titulo="Aulas de hoje" dica="toque para fazer a chamada">
      <ul className="feed">
        {vivas.map((aula) => {
          const feita = aula.chamada.feita;
          return (
            <li
              key={aula.marcacaoId}
              className="clickable"
              role="button"
              tabIndex={0}
              onClick={() => onChamada(aula.marcacaoId)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  onChamada(aula.marcacaoId);
                }
              }}
            >
              <span className="sq" style={{ background: 'var(--panel-3)' }}>
                <Icone nome="ball" />
              </span>
              <div>
                <b>
                  {horaNaArena(aula.inicio, consulta.data.fuso)} · {aula.titulo}
                </b>
                <small>
                  {aula.quadra.nome}
                  {aula.professor ? ` · ${aula.professor}` : ''}
                </small>
              </div>
              <div className="rt">
                {feita ? (
                  <Status situacao="ok">
                    {aula.chamada.presentes + aula.chamada.reposicoes} presentes ·{' '}
                    {aula.chamada.faltas} faltas
                  </Status>
                ) : (
                  <Status situacao="aguardando">
                    {pode('turmas', 'registrar_chamada') ? 'Fazer chamada' : 'Sem chamada'}
                  </Status>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    </Card>
  );
}

function CardDeRisco({
  consulta,
  turmas,
  onAbrirAluno,
}: {
  consulta: UseQueryResult<RiscoDeCancelamento>;
  turmas: readonly TurmaNaLista[];
  onAbrirAluno: (alunoId: string) => void;
}) {
  const { pode } = useSessao();
  const total = consulta.data?.alunos.length ?? 0;
  const horaDaTurma = new Map(
    turmas.map((t) => [
      t.titulo,
      t.grade ? `${diasCurtos(t.grade.diasSemana)} ${t.grade.horaInicio.slice(0, 2)}h` : '',
    ]),
  );

  return (
    <Card
      titulo="Risco de cancelamento"
      acao={
        consulta.data ? (
          <Status situacao={total > 0 ? 'atencao' : 'ok'}>
            {total === 1 ? '1 aluno' : `${total} alunos`}
          </Status>
        ) : undefined
      }
    >
      {consulta.isPending && <EsqueletoDeCard linhas={3} />}
      {consulta.isError && <Erro detalhe={mensagemDoErro(consulta.error)} />}
      {consulta.data && total === 0 && <Vazio>Ninguém com faltas seguidas agora.</Vazio>}
      {consulta.data && total > 0 && (
        <ul className="feed">
          {consulta.data.alunos.map((a) => (
            <li
              key={a.aluno.id}
              className={pode('alunos', 'ler') ? 'clickable' : undefined}
              onClick={pode('alunos', 'ler') ? () => onAbrirAluno(a.aluno.id) : undefined}
            >
              <Avatar nome={a.aluno.nome} />
              <div>
                <b>{a.aluno.nome}</b>
                <small>
                  {a.faltasSeguidas} faltas seguidas
                  {a.faturaVencida ? ' · fatura vencida' : ''}
                  {a.turmas[0]
                    ? ` · ${a.turmas[0].toLowerCase()} ${horaDaTurma.get(a.turmas[0]) ?? ''}`
                    : ''}
                </small>
              </div>
              <div className="rt">
                <b style={{ color: a.nivel === 'alto' ? 'var(--red-lt)' : 'var(--amber)' }}>
                  {a.nivel === 'alto' ? 'Alto' : 'Médio'}
                </b>
              </div>
            </li>
          ))}
        </ul>
      )}
      {/* 12 · planos */}
      {consulta.data && !consulta.data.faturasConsideradas && (
        <p className="sub" style={{ marginTop: 10 }}>
          Seu perfil não vê cobranças: o nível médio (duas faltas e fatura vencida) não aparece
          aqui.
        </p>
      )}
      {/* O botão de "enviar mensagem" saiu junto com o WhatsApp: não há
          canal de envio no produto. Quem está em risco aparece na lista
          acima, com o telefone na ficha — a ligação é da recepção. */}
    </Card>
  );
}
